import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { patientsApi, expressApi, schedulingApi } from '@/api';
import type { ExpressStatusResponse } from '@/api';
import { paymentsApi } from '@/api/payments-api';
import { TriageChatbot, TriageResult } from '@/components/express/TriageChatbot';
import { WaitingQueue } from '@/components/express/WaitingQueue';
import { PixQRCode } from '@/components/payments/PixQRCode';
import { Captcha } from '@/components/common/Captcha';
import { useExpressSocket } from '@/hooks/express';
import { playDoctorReadySound, playSuccessChime } from '@/lib/sound';
import { Button } from '@medikei/ui';
import { toast } from '@/components/ui/use-toast';
import {
  Zap,
  ChevronRight,
  Loader2,
  CheckCircle,
  AlertTriangle,
  CreditCard,
  QrCode,
  Calendar,
  PhoneCall,
  ShieldCheck,
  AlertCircle,
  RefreshCw,
  Lock,
  ExternalLink,
} from 'lucide-react';

type Step = 'payment' | 'triage' | 'waiting' | 'room' | 'consultation';

  const formatCpfMask = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  };

  const isCpfValid = (cpfValue: string) => {
    return validateCPF(cpfValue);
  };

function validateCPF(cpf: string): boolean {
  if (!cpf) return false;
  cpf = cpf.replace(/\D/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i), 10) * (10 - i);
  }
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;
  if (parseInt(cpf.charAt(9), 10) !== digit1) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i), 10) * (11 - i);
  }
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;

  return parseInt(cpf.charAt(10), 10) === digit2;
}

export function ExpressaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStep = searchParams.get('step') as Step | null;
  const expressId = searchParams.get('expressId');
  const urlAppointmentId = searchParams.get('appointmentId');

  const [step, setStep] = useState<Step>(initialStep || 'payment');
  const [patientProfile, setPatientProfile] = useState<{ id: string; name?: string; email?: string; cpf?: string | null } | null>(null);
  const [customCpf, setCustomCpf] = useState('');
  
  // Pricing
  const [price, setPrice] = useState(89);
  const [formattedPrice, setFormattedPrice] = useState('R$ 89,00');

  // Payment states
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'card'>('pix');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [pixData, setPixData] = useState<{
    paymentId: string;
    qrCode?: string;
    qrCodeUrl?: string;
    copyPaste?: string;
    checkoutUrl?: string;
  } | null>(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(initialStep === 'triage');
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const paymentPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Card form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardExp, setCardExp] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [selectedInstallment, setSelectedInstallment] = useState<number>(1);

  const { data: installmentData } = useQuery({
    queryKey: ['pagbank', 'installments', price],
    queryFn: () => paymentsApi.getPagBankInstallmentOptions(price),
    enabled: price > 0,
  });

  // Appointment & Queue states
  const [appointmentId, setAppointmentId] = useState<string | null>(urlAppointmentId || null);
  const [expressStatus, setExpressStatus] = useState<ExpressStatusResponse | null>(null);
  const queuePollingRef = useRef<NodeJS.Timeout | null>(null);
  const [queuePosition, setQueuePosition] = useState<number>(1);
  const [estimatedTime, setEstimatedTime] = useState<number>(8);
  const [doctorsOnline, setDoctorsOnline] = useState<number>(0);
  const [doctorFoundNotification, setDoctorFoundNotification] = useState<string | null>(null);

  // Emergency safety modal state
  const [emergencyAlert, setEmergencyAlert] = useState<{
    visible: boolean;
    triageResult?: TriageResult;
  }>({ visible: false });
  const [isAbortingEmergency, setIsAbortingEmergency] = useState(false);
  const [emergencyReceipt, setEmergencyReceipt] = useState<{
    refunded: boolean;
    refundAmount: number;
    isSamu: boolean;
    reason?: string;
  } | null>(null);

  const token = localStorage.getItem('accessToken');

  const {
    connected: socketConnected,
    onQueued,
    onDoctorFound,
    onDoctorAccepted,
    onTimeout,
    onCancelled,
    onRoomCreated,
    onConsultationStarted,
    onConsultationFinished,
  } = useExpressSocket({
    expressId: expressStatus?.expressId,
    token: token || undefined,
    enabled: step === 'waiting' && !!expressStatus?.expressId,
  });

  // Socket event listeners
  useEffect(() => {
    const cleanups: Array<() => void> = [];

    if (socketConnected && expressStatus?.expressId) {
      cleanups.push(
        onQueued((data) => {
          if (data.position !== undefined) setQueuePosition(data.position);
          if (data.estimatedTime !== undefined) setEstimatedTime(data.estimatedTime);
        })
      );

      cleanups.push(
        onDoctorFound((data) => {
          playDoctorReadySound();
          setDoctorFoundNotification(`Dr(a). ${data.doctorName} encontrou sua solicitação`);
          setExpressStatus((prev) =>
            prev ? { ...prev, doctorId: data.doctorId, doctorName: data.doctorName } : null
          );
        })
      );

      cleanups.push(
        onDoctorAccepted((data) => {
          playDoctorReadySound();
          if (queuePollingRef.current) {
            clearInterval(queuePollingRef.current);
            queuePollingRef.current = null;
          }
          setExpressStatus((prev) =>
            prev ? { ...prev, status: 'ACCEPTED', doctorId: data.doctorId, doctorName: data.doctorName, sessionId: data.sessionId } : null
          );
          setStep('room');
          const aptId = expressStatus?.appointmentId;
          setTimeout(() => {
            if (aptId) {
              navigate(`/telemedicine/waiting/${aptId}`);
            }
          }, 2000);
        })
      );

      cleanups.push(
        onTimeout(() => {
          setPaymentError('Tempo de espera excedido. Você pode estender por mais 15 minutos ou cancelar com estorno total.');
          setExpressStatus((prev) => (prev ? { ...prev, status: 'EXPIRED' } : null));
          if (queuePollingRef.current) {
            clearInterval(queuePollingRef.current);
            queuePollingRef.current = null;
          }
        })
      );

      cleanups.push(
        onCancelled(() => {
          setPaymentError('Consulta cancelada.');
          setExpressStatus((prev) => (prev ? { ...prev, status: 'CANCELLED' } : null));
          if (queuePollingRef.current) {
            clearInterval(queuePollingRef.current);
            queuePollingRef.current = null;
          }
          setTimeout(() => {
            setStep('payment');
            setAppointmentId(null);
            setExpressStatus(null);
          }, 2000);
        })
      );

      cleanups.push(
        onRoomCreated((data) => {
          playDoctorReadySound();
          setExpressStatus((prev) =>
            prev ? { ...prev, status: 'ROOM_CREATED', sessionId: data.sessionId, roomId: data.roomId } : null
          );
          if (queuePollingRef.current) {
            clearInterval(queuePollingRef.current);
            queuePollingRef.current = null;
          }
          setStep('room');
          const aptId = expressStatus?.appointmentId;
          setTimeout(() => {
            if (aptId) {
              navigate(`/telemedicine/waiting/${aptId}`);
            }
          }, 1800);
        })
      );

      cleanups.push(
        onConsultationStarted(() => {
          setStep('consultation');
        })
      );

      cleanups.push(
        onConsultationFinished(() => {
          if (queuePollingRef.current) {
            clearInterval(queuePollingRef.current);
            queuePollingRef.current = null;
          }
          setExpressStatus((prev) => (prev ? { ...prev, status: 'FINISHED' } : null));
        })
      );
    }

    return () => cleanups.forEach((fn) => fn());
  }, [
    socketConnected,
    expressStatus?.expressId,
    onQueued,
    onDoctorFound,
    onDoctorAccepted,
    onTimeout,
    onCancelled,
    onRoomCreated,
    onConsultationStarted,
    onConsultationFinished,
    navigate,
    expressStatus?.appointmentId,
  ]);

  // Initial loads com polling sincronizado de médicos online
  useEffect(() => {
    loadPatientProfile();
    loadDoctorsOnline();
    loadPricing();

    const doctorsPollInterval = setInterval(() => {
      loadDoctorsOnline();
    }, 5000);

    if (expressId) {
      resumeExpress(expressId);
    } else if (urlAppointmentId) {
      checkReturnedPayment(urlAppointmentId);
    }

    return () => {
      clearInterval(doctorsPollInterval);
      if (queuePollingRef.current) clearInterval(queuePollingRef.current);
      if (paymentPollingRef.current) clearInterval(paymentPollingRef.current);
    };
  }, []);

  const loadPricing = async () => {
    try {
      console.log('[EXPRESS-CHECKOUT-FE] 1. Carregando precificação via expressApi.getPricing()...');
      const res = await expressApi.getPricing();
      console.log('[EXPRESS-CHECKOUT-FE] 1. Resposta do getPricing():', res);
      if (res?.price) {
        const safePrice = res.price >= 1000 ? res.price / 100 : res.price;
        const formatted = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(safePrice);
        setPrice(safePrice);
        setFormattedPrice(formatted);
      }
    } catch (err) {
      console.warn('[EXPRESS-CHECKOUT-FE] 1. Falha ao carregar getPricing(), usando fallback R$ 89:', err);
      setPrice(89);
      setFormattedPrice('R$ 89,00');
    }
  };

  const loadPatientProfile = async () => {
    try {
      console.log('[EXPRESS-CHECKOUT-FE] 2. Carregando dados do paciente via patientsApi.getProfile()...');
      const profile = await patientsApi.getProfile();
      console.log('[EXPRESS-CHECKOUT-FE] 2. Perfil do paciente retornado:', profile);
      setPatientProfile({ id: profile.id, name: profile.name || '', email: profile.email, cpf: profile.cpf });
      if (profile.cpf) {
        setCustomCpf(profile.cpf);
      }
    } catch (err) {
      console.warn('[EXPRESS-CHECKOUT-FE] 2. Perfil não autenticado ou indisponível:', err);
      setPatientProfile({ id: 'anonymous', name: 'Paciente', email: 'paciente@medikei.com.br' });
    }
  };

  const loadDoctorsOnline = async () => {
    try {
      const response = await expressApi.getDoctorsOnline();
      let count = response?.count || 0;
      if (count === 0) {
        // Fallback síncrono para métrica em tempo real da fila
        const queueRes = await expressApi.getQueueStatus();
        if (queueRes?.availableDoctors && queueRes.availableDoctors > 0) {
          count = queueRes.availableDoctors;
        }
      }
      setDoctorsOnline(count);
      return count;
    } catch {
      try {
        const queueRes = await expressApi.getQueueStatus();
        const count = queueRes?.availableDoctors || 0;
        setDoctorsOnline(count);
        return count;
      } catch {
        return 0;
      }
    }
  };

  const resumeExpress = async (id: string) => {
    try {
      const status = await expressApi.getStatus(id);
      setExpressStatus(status);
      if (status.status === 'WAITING' || status.status === 'SEARCHING') {
        setAppointmentId(status.appointmentId);
        setStep('waiting');
        startQueuePolling(id);
      } else if (status.status === 'ROOM_CREATED' || status.status === 'ACCEPTED') {
        setStep('room');
        setTimeout(() => {
          navigate(`/telemedicine/waiting/${status.appointmentId}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to resume express:', error);
    }
  };

  const checkReturnedPayment = async (aptId: string) => {
    try {
      console.info('[EXPRESS-CHECKOUT-FE] Verificando retorno de pagamento para consulta:', aptId);
      const statusRes = await paymentsApi.getPaymentByAppointment(aptId).catch(() => null);
      if (statusRes?.status === 'APPROVED' || statusRes?.status === 'PAID') {
        console.info('[EXPRESS-CHECKOUT-FE] Pagamento validado com sucesso após retorno!');
        setAppointmentId(aptId);
        setPaymentConfirmed(true);
        setStep('triage');
      } else {
        // Se ainda estiver pendente, inicia polling curto para aguardar conciliação
        setAppointmentId(aptId);
        startPaymentPolling(statusRes?.paymentId || aptId);
      }
    } catch (err) {
      console.debug('Erro ao checar status de retorno:', err);
    }
  };

  // Payment Execution & Polling
  const handleInitiatePayment = async () => {
    console.info('[EXPRESS-CHECKOUT-FE] >>> Iniciando checkout de consulta expressa...', {
      customCpf,
      profileCpf: patientProfile?.cpf,
      patientId: patientProfile?.id,
      patientName: patientProfile?.name,
      patientEmail: patientProfile?.email,
      price,
      paymentMethod,
    });

    // customCpf (inserido diretamente na tela pelo usuário) tem prioridade sobre o cache de perfil
    const enteredCpf = (customCpf || '').trim();
    const profileCpf = (patientProfile?.cpf || '').trim();
    const activeCpf = (enteredCpf || profileCpf).replace(/\D/g, '');

    console.info('[EXPRESS-CHECKOUT-FE] Avaliação do CPF:', {
      enteredCpf,
      profileCpf,
      activeCpf,
      activeCpfDigits: activeCpf.length,
      isValidCPF: validateCPF(activeCpf),
    });

    if (!activeCpf || !validateCPF(activeCpf)) {
      console.warn('[EXPRESS-CHECKOUT-FE] Bloqueado: CPF inválido ou incompleto:', activeCpf);
      setPaymentError('Por favor, informe um CPF válido para emissão do recibo e atendimento.');
      return;
    }

    if (doctorsOnline === 0) {
      console.warn('[EXPRESS-CHECKOUT-FE] Bloqueado: Nenhum médico plantonista online.');
      setPaymentError(
        'Não há médicos plantonistas disponíveis no momento para pronto atendimento. Por favor, agende um horário marcado ou aguarde um plantonista conectar.'
      );
      return;
    }

    setPaymentProcessing(true);
    setPaymentError(null);

    try {
      const safePaymentPrice = price >= 1000 ? price / 100 : (price > 0 ? price : 89);
      console.info('[EXPRESS-CHECKOUT-FE] Valor final seguro da consulta:', safePaymentPrice);

      // 1. Create Appointment
      let newAppointmentId = appointmentId;
      let assignedDoctorId = 'auto-matched';
      if (!newAppointmentId) {
        console.info('[EXPRESS-CHECKOUT-FE] Disparando POST /appointments/express...');
        const appointmentResult = await schedulingApi.createExpressAppointment({
          specialtyId: undefined,
          doctorId: undefined,
          chiefComplaint: 'Consulta Expressa - Pronto Atendimento',
          price: safePaymentPrice,
        });
        console.info('[EXPRESS-CHECKOUT-FE] Consulta expressa criada com sucesso:', appointmentResult);
        newAppointmentId = appointmentResult.appointmentId;
        if (appointmentResult.doctorId) {
          assignedDoctorId = appointmentResult.doctorId;
        }
        setAppointmentId(newAppointmentId);
      }

      try {
        if (paymentMethod === 'card') {
          console.info('[EXPRESS-CHECKOUT-FE] Processando pagamento com Cartão via Order API...');
          let encryptedCard: string | undefined;

          // Se o SDK do PagBank estiver disponível e houver chave pública, criptografa no cliente (Capítulo #15)
          try {
            const config = await paymentsApi.getPagBankPublicConfig().catch(() => null);
            if (config?.publicKey && (window as any).PagSeguro?.encryptCard) {
              const [cleanMonth, cleanYear] = cardExp.split('/').map((s) => s.trim());
              encryptedCard = (window as any).PagSeguro.encryptCard({
                publicKey: config.publicKey,
                holder: cardHolder || patientProfile?.name || 'Paciente',
                number: cardNumber.replace(/\D/g, ''),
                expMonth: (cleanMonth || '12').padStart(2, '0'),
                expYear: cleanYear && cleanYear.length === 2 ? `20${cleanYear}` : cleanYear || '2030',
                securityCode: cardCvv.trim(),
              })?.encryptedCard;
            }
          } catch (encErr) {
            console.warn('[EXPRESS-CHECKOUT-FE] Aviso na criptografia client-side:', encErr);
          }

          const cardRes = await paymentsApi.createCardPayment({
            appointmentId: newAppointmentId,
            patientId: patientProfile?.id || 'me',
            doctorId: assignedDoctorId,
            amount: safePaymentPrice,
            description: 'Consulta Expressa - Atendimento Imediato',
            patientName: cardHolder || patientProfile?.name || 'Paciente',
            patientEmail: patientProfile?.email || 'paciente@medikei.com.br',
            patientCpf: activeCpf,
            installments: Number(selectedInstallment) || 1,
            encryptedCard,
            securityCode: cardCvv.trim(),
            captchaToken,
          });

          console.info('[EXPRESS-CHECKOUT-FE] Resposta do cartão recebida:', cardRes);

          if (cardRes?.checkoutUrl) {
            setTimeout(() => {
              window.location.href = cardRes.checkoutUrl!;
            }, 800);
          } else {
            if (cardRes?.paymentId) {
              await paymentsApi.confirmPayment(cardRes.paymentId).catch(() => {});
            }
            setPaymentConfirmed(true);
            playSuccessChime();
            setTimeout(() => {
              setStep('triage');
            }, 1200);
          }
        } else {
          console.info('[EXPRESS-CHECKOUT-FE] Disparando pagamento PIX via Order API (Capítulo #23)...', {
            appointmentId: newAppointmentId,
            doctorId: assignedDoctorId,
            amount: safePaymentPrice,
            activeCpf,
          });

          const payRes = await paymentsApi.createPixPayment({
            appointmentId: newAppointmentId,
            patientId: patientProfile?.id || 'me',
            doctorId: assignedDoctorId,
            amount: safePaymentPrice,
            description: 'Consulta Expressa - Medikei',
            patientName: patientProfile?.name || 'Paciente Medikei',
            patientEmail: patientProfile?.email || 'paciente@medikei.com.br',
            patientCpf: activeCpf,
            captchaToken,
            returnUrl: `${window.location.origin}/expressa?step=triage&appointmentId=${newAppointmentId}`,
          });

          console.info('[EXPRESS-CHECKOUT-FE] Resposta do PIX recebida:', payRes);

          setPixData({
            paymentId: payRes.paymentId,
            qrCode: payRes.qrCode,
            qrCodeUrl: payRes.qrCodeUrl,
            copyPaste: payRes.qrCode || payRes.checkoutUrl,
            checkoutUrl: payRes.checkoutUrl,
          });

          startPaymentPolling(payRes.paymentId);

          if (payRes.checkoutUrl) {
            setTimeout(() => {
              window.location.href = payRes.checkoutUrl!;
            }, 800);
          }
        }
      } catch (payErr: any) {
        console.error('[EXPRESS-CHECKOUT-FE] Falha ao gerar pagamento no PagBank:', payErr);
        const errMessage = payErr?.response?.data?.message || payErr?.message || 'Falha na comunicação com o PagBank. Verifique as credenciais no servidor.';
        setPaymentError(errMessage);
      }
    } catch (error: any) {
      console.error('[EXPRESS-CHECKOUT-FE] Erro fatal no handleInitiatePayment:', error);
      const apiMsg = error?.response?.data?.message || error?.message;
      setPaymentError(
        typeof apiMsg === 'string'
          ? apiMsg
          : 'Falha ao iniciar pagamento. Verifique os dados e tente novamente.'
      );
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleSimulatePixApproval = async () => {
    try {
      setPaymentProcessing(true);
      const targetId = pixData?.paymentId || appointmentId;
      if (targetId) {
        await paymentsApi.confirmPayment(targetId).catch(() => {});
      }
      if (paymentPollingRef.current) {
        clearInterval(paymentPollingRef.current);
        paymentPollingRef.current = null;
      }
      setPaymentConfirmed(true);
      playSuccessChime();
      setTimeout(() => {
        setStep('triage');
      }, 1200);
    } catch (err) {
      console.warn('Simulação de aprovação PIX em teste:', err);
      if (paymentPollingRef.current) {
        clearInterval(paymentPollingRef.current);
        paymentPollingRef.current = null;
      }
      setPaymentConfirmed(true);
      playSuccessChime();
      setTimeout(() => {
        setStep('triage');
      }, 1200);
    } finally {
      setPaymentProcessing(false);
    }
  };

  const startPaymentPolling = (paymentId: string) => {
    if (paymentPollingRef.current) clearInterval(paymentPollingRef.current);

    paymentPollingRef.current = setInterval(async () => {
      try {
        const statusRes = await paymentsApi.getPaymentStatus(paymentId);
        if (statusRes.status === 'APPROVED' || statusRes.status === 'PAID') {
          if (paymentPollingRef.current) {
            clearInterval(paymentPollingRef.current);
            paymentPollingRef.current = null;
          }
          setPaymentConfirmed(true);
          playSuccessChime();
          setTimeout(() => {
            setStep('triage');
          }, 1500);
        } else if (statusRes.status === 'FAILED' || statusRes.status === 'CANCELLED') {
          if (paymentPollingRef.current) {
            clearInterval(paymentPollingRef.current);
            paymentPollingRef.current = null;
          }
          setPaymentError('O pagamento foi recusado ou cancelado. Tente novamente.');
          setPixData(null);
        }
      } catch (err) {
        console.debug('Payment polling check:', err);
      }
    }, 3000);
  };

  // Queue Polling
  const startQueuePolling = (id: string) => {
    if (queuePollingRef.current) {
      clearInterval(queuePollingRef.current);
      queuePollingRef.current = null;
    }

    queuePollingRef.current = setInterval(async () => {
      try {
        const [status, queueData] = await Promise.all([
          expressApi.getStatus(id),
          expressApi.getQueuePosition(id).catch(() => null),
        ]);

        setExpressStatus(status);
        if (queueData) {
          setQueuePosition(queueData.position);
          setDoctorsOnline(queueData.doctorsOnline);
          if (queueData.doctorsOnline > 0) {
            setEstimatedTime(Math.max(3, Math.min(15, Math.round(15 / queueData.doctorsOnline))));
          }
        }

        if ((status.status === 'ACCEPTED' || status.status === 'ROOM_CREATED') && status.sessionId) {
          if (queuePollingRef.current) {
            clearInterval(queuePollingRef.current);
            queuePollingRef.current = null;
          }
          playDoctorReadySound();
          setStep('room');
          setTimeout(() => {
            navigate(`/telemedicine/waiting/${status.appointmentId}`);
          }, 1800);
        } else if (status.status === 'EXPIRED' || status.status === 'CANCELLED') {
          if (queuePollingRef.current) {
            clearInterval(queuePollingRef.current);
            queuePollingRef.current = null;
          }
          setPaymentError('Tempo de espera expirado. Clique para solicitar reembolso.');
        }
      } catch (error) {
        console.debug('Queue polling check:', error);
      }
    }, 4000);
  };

  // Triage Completion with Safety Gate
  const handleTriageComplete = async (result: TriageResult) => {
    // Clinical Safety Gate: Intercept emergencies
    const isEmergency =
      result.riskClassification === 'EMERGENCY' ||
      result.requiresImmediateCare === true ||
      (result.redFlags && result.redFlags.length > 1);

    if (isEmergency) {
      setEmergencyAlert({ visible: true, triageResult: result });
      return;
    }

    await startExpressQueue(result);
  };

  const handleProceedDespiteEmergency = async () => {
    const result = emergencyAlert.triageResult;
    setEmergencyAlert({ visible: false });
    if (result) {
      await startExpressQueue(result);
    }
  };

  const handleAbortEmergency = async (isSamu: boolean) => {
    if (!appointmentId) return;
    try {
      setIsAbortingEmergency(true);
      const reason = isSamu
        ? 'EMERGÊNCIA MÉDICA - ACIONADO SAMU 192'
        : 'EMERGÊNCIA MÉDICA - ENCAMINHADO AO PRONTO-SOCORRO / UPA';
      const res = await expressApi.abortForEmergency({
        appointmentId,
        reason,
      });
      setEmergencyAlert({ visible: false });
      setEmergencyReceipt({
        refunded: res.refunded,
        refundAmount: res.refundAmount || price,
        isSamu,
        reason,
      });
      if (isSamu) {
        setTimeout(() => {
          window.location.href = 'tel:192';
        }, 1200);
      }
    } catch (err: any) {
      toast({
        title: 'Erro ao processar estorno',
        description: err?.message || 'Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsAbortingEmergency(false);
    }
  };

  const startExpressQueue = useCallback(
    async (triageResult?: TriageResult) => {
      if (!appointmentId) return;

      try {
        const doctorsCount = await loadDoctorsOnline();
        const status = triageResult
          ? await expressApi.startExpressWithTriage({
              appointmentId,
              chiefComplaint: triageResult.chiefComplaint || 'Consulta Expressa',
              triageData: {
                summary: triageResult.summary,
                riskClassification: triageResult.riskClassification,
                redFlags: triageResult.redFlags || [],
                recommendedSpecialty: triageResult.recommendedSpecialty,
                symptomIntensity: triageResult.symptomIntensity,
                symptomDuration: triageResult.symptomDuration,
                symptomDetail: triageResult.summary,
                medicalHistory: triageResult.relevantHistory?.join(', '),
                currentMedications: triageResult.currentMedications,
              },
            })
          : await expressApi.startExpress({
              appointmentId,
              chiefComplaint: 'Consulta Expressa',
            });

        setExpressStatus(status);
        setStep('waiting');
        setQueuePosition(1);
        setEstimatedTime(doctorsCount > 0 ? Math.max(3, Math.min(15, Math.round(15 / doctorsCount))) : 8);
        startQueuePolling(status.expressId);
      } catch (error) {
        console.error('Failed to start express queue:', error);
        setPaymentError('Falha ao entrar na fila. Tente novamente.');
      }
    },
    [appointmentId]
  );

  const handleCancelExpress = async () => {
    const targetAptId = appointmentId || expressStatus?.appointmentId;
    if (targetAptId) {
      try {
        await expressApi.abortForEmergency({
          appointmentId: targetAptId,
          reason: 'DESISTÊNCIA DO PACIENTE NA FILA DE ESPERA',
        });
        toast({
          title: 'Chamado cancelado com sucesso',
          description: 'O estorno integral do valor pago foi solicitado junto à operadora/PIX.',
          variant: 'success',
        });
      } catch (error) {
        console.error('Failed to cancel express with refund:', error);
      }
    } else if (expressStatus?.expressId) {
      try {
        await expressApi.cancelExpress(expressStatus.expressId, 'Patient cancelled');
      } catch (error) {
        console.error('Failed to cancel express:', error);
      }
    }
    if (queuePollingRef.current) clearInterval(queuePollingRef.current);
    if (paymentPollingRef.current) clearInterval(paymentPollingRef.current);

    setStep('payment');
    setAppointmentId(null);
    setExpressStatus(null);
    setPixData(null);
    setPaymentConfirmed(false);
  };

  // ----------------------------------------------------
  // RENDERERS
  // ----------------------------------------------------

  const renderPayment = () => (
    <div className="w-full max-w-lg mx-auto">
      <div className="bg-card rounded-2xl sm:rounded-3xl border border-neutral-200 dark:border-neutral-800 shadow-sm p-4 sm:p-6 md:p-8">
        <div className="text-center mb-5 sm:mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-primary/10 text-primary rounded-2xl mb-2.5 sm:mb-3">
            <Zap className="h-6 w-6 sm:h-7 sm:w-7" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Telemedicina Expressa</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">Pronto Atendimento Online Imediato</p>
        </div>

        {/* Doctors online badge / Zero doctors warning */}
        {doctorsOnline > 0 ? (
          <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl mb-5 sm:mb-6 text-xs sm:text-sm text-emerald-800">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-medium">Plantonistas online agora</span>
            </div>
            <span className="font-bold">{doctorsOnline} médicos</span>
          </div>
        ) : (
          <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl mb-5 sm:mb-6 text-xs sm:text-sm text-amber-900 dark:text-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold mb-1">Nenhum plantonista online no momento</p>
                <p className="text-xs text-amber-800 dark:text-amber-300 mb-2.5 leading-relaxed">
                  Para sua segurança, o checkout do pronto atendimento fica bloqueado enquanto não houver médicos de plantão conectados, evitando cobranças sem atendimento garantido.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={() => navigate('/agendamentos')}
                    variant="outline"
                    size="sm"
                    className="bg-white dark:bg-neutral-900 border-amber-300 text-amber-900 dark:text-amber-200 text-xs font-semibold"
                  >
                    <Calendar className="w-3.5 h-3.5 mr-1.5" />
                    Ver Agenda Completa
                  </Button>
                  <Button
                    onClick={() => loadDoctorsOnline()}
                    variant="outline"
                    size="sm"
                    className="bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/40 border-amber-300 text-amber-900 dark:text-amber-200 text-xs font-semibold"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    Atualizar Plantão
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Price Box - Reorganizado com layout responsivo e respiração visual para evitar embolamento */}
        <div className="bg-slate-50/90 dark:bg-slate-900/60 rounded-2xl p-3.5 sm:p-5 mb-5 sm:mb-6 border border-slate-200/90 dark:border-neutral-800 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4 transition-all">
          <div className="space-y-1">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-blue-100/70 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider border border-blue-200/60 dark:border-blue-800/60">
              Valor da Consulta
            </span>
            <p className="text-xs text-muted-foreground font-medium">
              Pagamento único · Sem mensalidade ou taxa de adesão
            </p>
          </div>
          <div className="text-left sm:text-right shrink-0">
            <div className="text-2xl sm:text-3xl font-black text-foreground tracking-tight whitespace-nowrap">
              {formattedPrice}
            </div>
            <span className="text-[10px] sm:text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 block mt-0.5">
              Acesso imediato ao plantão
            </span>
          </div>
        </div>

        {/* CPF Verification */}
        {(!patientProfile?.cpf || !validateCPF(patientProfile.cpf)) && (
          <div className="mb-5 sm:mb-6 p-3.5 sm:p-4 bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl">
            <label className="text-xs font-semibold text-blue-900 dark:text-blue-200 block mb-1.5 flex items-center justify-between gap-2">
              <span>Informe seu CPF para receita e prontuário:</span>
              {customCpf.replace(/\D/g, '').length === 11 && (
                <span className={isCpfValid(customCpf) ? 'text-emerald-600 dark:text-emerald-400 font-bold text-[10px] sm:text-[11px] shrink-0' : 'text-red-500 font-bold text-[10px] sm:text-[11px] shrink-0'}>
                  {isCpfValid(customCpf) ? '✓ Válido' : '✕ Inválido'}
                </span>
              )}
            </label>
            <input
              type="text"
              placeholder="000.000.000-00"
              maxLength={14}
              value={customCpf}
              onChange={(e) => {
                const formatted = formatCpfMask(e.target.value);
                setCustomCpf(formatted);
                if (paymentError && isCpfValid(formatted)) {
                  setPaymentError(null);
                }
              }}
              className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>
        )}

        {paymentError && (
          <div className="mb-5 sm:mb-6 p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 rounded-2xl text-xs text-amber-900 dark:text-amber-200 space-y-3">
            <div className="flex items-start gap-2.5">
              <AlertCircle className="w-5 h-5 shrink-0 text-amber-600 mt-0.5" />
              <div className="space-y-1">
                <p className="font-bold text-sm text-amber-950 dark:text-amber-100">
                  {paymentError.includes('Allowlist') || paymentError.includes('allowlist')
                    ? 'Conta PagBank em Processo de Homologação (Allowlist)'
                    : 'Aviso sobre o Pagamento'}
                </p>
                <p className="leading-relaxed">
                  {paymentError}
                </p>
              </div>
            </div>

            <div className="pt-2 border-t border-amber-200/60 dark:border-amber-800/60 space-y-2">
              <p className="text-[11px] text-amber-800 dark:text-amber-300">
                💡 Deseja continuar o atendimento agora em ambiente de teste?
              </p>
              <Button
                type="button"
                onClick={handleSimulatePixApproval}
                disabled={paymentProcessing}
                variant="outline"
                size="sm"
                className="w-full text-xs bg-amber-100 hover:bg-amber-200 border-amber-400 text-amber-950 font-bold shadow-xs cursor-pointer"
              >
                ⚡ Simular Aprovação do Pagamento (Avançar para Triagem)
              </Button>
            </div>
          </div>
        )}

        {/* ACTIVE PIX / PAGBANK CHECKOUT DISPLAY */}
        {pixData && !paymentConfirmed && (
          <div className="mb-5 sm:mb-6 p-5 sm:p-6 bg-linear-to-b from-emerald-50/70 to-white dark:from-slate-900/60 dark:to-slate-950 border border-emerald-200/80 dark:border-emerald-800/40 rounded-3xl text-center space-y-4 shadow-sm animate-in fade-in">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100/80 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>Ambiente Oficial PagBank</span>
            </div>

            <div className="space-y-1.5">
              <h3 className="font-bold text-base sm:text-lg text-foreground tracking-tight">
                Pagamento para a Medikei processado pela PagBank
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                Você será direcionado para o checkout oficial e exclusivo do PagBank com opções de <strong className="text-foreground">PIX instantâneo</strong>, <strong className="text-foreground">Cartão de Crédito</strong> ou <strong className="text-foreground">Boleto</strong>.
              </p>
            </div>

            {pixData.checkoutUrl ? (
              <div className="p-4 bg-white dark:bg-slate-900 rounded-2xl border border-border shadow-xs space-y-3">
                <div className="flex items-center justify-between text-xs sm:text-sm border-b border-border pb-2.5">
                  <span className="text-muted-foreground">Valor do Atendimento:</span>
                  <span className="font-bold text-foreground text-base">{formattedPrice}</span>
                </div>

                <div className="flex flex-col gap-2 pt-1">
                  <a
                    href={pixData.checkoutUrl}
                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl sm:rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-md transition-all cursor-pointer"
                  >
                    <span>Ir para o Pagamento Seguro no PagBank</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>

                  <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />
                    <span>Redirecionando em instantes... ou clique no botão acima.</span>
                  </p>
                </div>
              </div>
            ) : pixData.qrCode || pixData.qrCodeUrl ? (
              <PixQRCode
                qrCodeUrl={pixData.qrCodeUrl}
                qrCode={pixData.qrCode}
                copyPaste={pixData.copyPaste}
              />
            ) : null}

            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-1">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
              <span>Aguardando identificação bancária automática...</span>
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <Button
                type="button"
                onClick={handleSimulatePixApproval}
                disabled={paymentProcessing}
                variant="outline"
                size="sm"
                className="w-full text-xs bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-900 font-semibold"
              >
                ⚡ Simular Aprovação do Pagamento (Ambiente de Testes)
              </Button>

              <Button
                onClick={() => {
                  if (paymentPollingRef.current) clearInterval(paymentPollingRef.current);
                  setPixData(null);
                }}
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground"
              >
                Cancelar e voltar
              </Button>
            </div>
          </div>
        )}

        {/* PAYMENT CONFIRMED ANIMATION */}
        {paymentConfirmed && (
          <div className="mb-5 sm:mb-6 p-5 sm:p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-2">
            <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-600 mx-auto animate-bounce" />
            <h3 className="font-bold text-emerald-900 text-base sm:text-lg">Pagamento Aprovado!</h3>
            <p className="text-xs text-emerald-700">Iniciando a triagem clínica inteligente...</p>
          </div>
        )}

        {/* PAYMENT FORM SELECTION */}
        {!pixData && !paymentConfirmed && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
              <button
                type="button"
                onClick={() => setPaymentMethod('pix')}
                className={`p-3 sm:p-3.5 rounded-xl border flex flex-col items-center gap-1.5 sm:gap-2 font-semibold text-xs sm:text-sm transition-all cursor-pointer ${
                  paymentMethod === 'pix'
                    ? 'border-primary bg-primary/5 text-primary ring-2 ring-primary/20 shadow-xs'
                    : 'border-neutral-200 hover:bg-neutral-50 text-neutral-700'
                }`}
              >
                <QrCode className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>PIX Instantâneo</span>
              </button>

              <button
                type="button"
                onClick={() => setPaymentMethod('card')}
                className={`p-3 sm:p-3.5 rounded-xl border flex flex-col items-center gap-1.5 sm:gap-2 font-semibold text-xs sm:text-sm transition-all cursor-pointer ${
                  paymentMethod === 'card'
                    ? 'border-primary bg-primary/5 text-primary ring-2 ring-primary/20 shadow-xs'
                    : 'border-neutral-200 hover:bg-neutral-50 text-neutral-700'
                }`}
              >
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Cartão de Crédito</span>
              </button>
            </div>

            {/* Credit Card Inputs */}
            {paymentMethod === 'card' && (
              <div className="p-3.5 sm:p-4 bg-muted/20 border border-neutral-200 rounded-xl space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground font-medium block mb-1">
                    Número do Cartão
                  </label>
                  <input
                    type="text"
                    placeholder="0000 0000 0000 0000"
                    maxLength={19}
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value)}
                    className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-neutral-300 bg-white"
                  />
                </div>

                <div>
                  <label className="text-xs text-muted-foreground font-medium block mb-1">
                    Nome impresso no cartão
                  </label>
                  <input
                    type="text"
                    placeholder="NOME COMPLETO"
                    value={cardHolder}
                    onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
                    className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-neutral-300 bg-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground font-medium block mb-1">
                      Validade (MM/AA)
                    </label>
                    <input
                      type="text"
                      placeholder="MM/AA"
                      maxLength={5}
                      value={cardExp}
                      onChange={(e) => setCardExp(e.target.value)}
                      className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-neutral-300 bg-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground font-medium block mb-1">
                      CVV
                    </label>
                    <input
                      type="password"
                      placeholder="123"
                      maxLength={4}
                      value={cardCvv}
                      onChange={(e) => setCardCvv(e.target.value)}
                      className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-neutral-300 bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground font-medium block mb-1">
                    Opções de Parcelamento
                  </label>
                  <select
                    value={selectedInstallment}
                    onChange={(e) => setSelectedInstallment(Number(e.target.value))}
                    className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-neutral-300 bg-white font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                  >
                    {installmentData?.options && installmentData.options.length > 0 ? (
                      installmentData.options.map((opt) => (
                        <option key={opt.numero} value={opt.numero}>
                          {opt.descricao}
                        </option>
                      ))
                    ) : (
                      <option value={1}>1x de {formattedPrice} (sem juros)</option>
                    )}
                  </select>
                </div>
              </div>
            )}

            {doctorsOnline === 0 ? (
              <div className="space-y-2 pt-1">
                <Button
                  disabled
                  className="w-full py-3.5 sm:py-4 text-sm sm:text-base font-semibold shadow-none gap-2 rounded-xl sm:rounded-full bg-slate-200 dark:bg-neutral-800 text-slate-500 dark:text-neutral-400 cursor-not-allowed border border-slate-300 dark:border-neutral-700"
                >
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 shrink-0" />
                  <span>Pagamento Suspenso: Sem Plantonistas Online</span>
                </Button>
                <p className="text-[11px] text-center text-amber-700 dark:text-amber-400 leading-tight">
                  O checkout de pronto atendimento será liberado automaticamente assim que um médico plantonista conectar.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} />
                <Button
                  onClick={handleInitiatePayment}
                  disabled={paymentProcessing}
                  loading={paymentProcessing}
                  className="w-full py-4 text-sm sm:text-base font-bold shadow-md gap-2 rounded-xl sm:rounded-full cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Pagar com Checkout PagBank ({formattedPrice})</span>
                </Button>

                <div className="p-3 bg-neutral-50 dark:bg-slate-900/50 border border-neutral-200 dark:border-neutral-800 rounded-xl text-center space-y-1">
                  <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    <span>Pagamento para a Medikei processado pela PagBank</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Ambiente oficial certificado. Aceita PIX instantâneo, Cartão de Crédito e Boleto com segurança.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-5 sm:mt-6 pt-4 border-t border-neutral-100">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <span>Pagamento para a Medikei processado pela PagBank • Ambiente Criptografado</span>
        </div>
      </div>
    </div>
  );

  const renderTriage = () => {
    // Tela de Recibo de Cancelamento e Estorno por Emergência
    if (emergencyReceipt) {
      return (
        <div className="w-full max-w-lg mx-auto px-1 sm:px-0 animate-in fade-in">
          <div className="bg-card rounded-3xl border border-border shadow-xl p-6 sm:p-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-500/20 shadow-xs">
              <CheckCircle className="w-8 h-8" />
            </div>

            <div className="space-y-2">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-300">
                <span>Atendimento Cancelado com Sucesso</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-foreground tracking-tight">
                Estorno Total Solicitado
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                O valor de <strong className="text-foreground">{formattedPrice}</strong> foi cancelado e estornado integralmente para a sua forma de pagamento original (PIX / Cartão).
              </p>
            </div>

            {/* Alerta de Segurança Presencial */}
            <div className="p-4 rounded-2xl bg-red-500/10 border-2 border-red-500/30 text-left space-y-2 text-red-900 dark:text-red-200">
              <div className="flex items-center gap-2 font-bold text-sm text-red-700 dark:text-red-400">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Priorize seu socorro imediato presencial!</span>
              </div>
              <p className="text-xs leading-relaxed opacity-90">
                Seus sintomas indicam a necessidade de avaliação física presencial. Se ainda não realizou a ligação, entre em contato imediatamente com os serviços de urgência:
              </p>
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <a
                  href="tel:192"
                  className="flex-1 py-2.5 px-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-sm transition-all"
                >
                  <PhoneCall className="w-3.5 h-3.5" />
                  <span>Ligar 192 (SAMU)</span>
                </a>
                <a
                  href="tel:193"
                  className="py-2.5 px-3 bg-red-700 hover:bg-red-800 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-sm transition-all"
                >
                  <span>193 (Bombeiros)</span>
                </a>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={() => {
                setEmergencyReceipt(null);
                setStep('payment');
                setAppointmentId(null);
                navigate('/');
              }}
              className="w-full rounded-xl font-bold text-xs h-11"
            >
              Voltar para a Página Inicial
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full max-w-2xl mx-auto px-1 sm:px-0">
        <button
          onClick={() => setStep('payment')}
          className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground mb-3 text-xs sm:text-sm font-medium cursor-pointer"
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          <span>Voltar</span>
        </button>

        <div className="mb-4 sm:mb-6 text-left">
          <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-1">Triagem Clínica com IA</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Responda com brevidade para que o médico plantonista tenha o seu histórico preparado antes de conectar.
          </p>
        </div>

        <TriageChatbot
          patientId={patientProfile?.id}
          appointmentId={appointmentId || undefined}
          onComplete={handleTriageComplete}
          onCancel={() => setStep('payment')}
        />

        {/* Emergency Alert Modal com Estorno Automático */}
        {emergencyAlert.visible && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-white rounded-3xl max-w-md w-full p-6 space-y-4 border-2 border-red-500 shadow-2xl text-slate-800">
              <div className="flex items-center gap-3 text-red-600">
                <AlertTriangle className="w-8 h-8 shrink-0 animate-pulse" />
                <h3 className="text-lg font-bold">ALERTA DE EMERGÊNCIA MÉDICA</h3>
              </div>

              <p className="text-sm text-neutral-700 leading-relaxed">
                Com base nos sintomas informados, você pode estar apresentando um quadro que necessita de
                <strong> atendimento médico presencial imediato</strong>.
              </p>

              <div className="p-3.5 bg-red-50 rounded-xl border border-red-200 text-xs text-red-800 space-y-1">
                <p className="font-semibold">Recomendação clínica do protocolo:</p>
                <p>• Acione imediatamente o serviço de emergência (SAMU 192 ou Bombeiros 193).</p>
                <p>• Ou dirija-se à Unidade de Pronto Atendimento (UPA) mais próxima.</p>
              </div>

              <div className="space-y-2 pt-2">
                {/* Botão 1: SAMU 192 com estorno automático */}
                <Button
                  onClick={() => handleAbortEmergency(true)}
                  disabled={isAbortingEmergency}
                  className="w-full py-3.5 px-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-md text-xs cursor-pointer"
                >
                  {isAbortingEmergency ? (
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                  ) : (
                    <PhoneCall className="w-4 h-4 text-white" />
                  )}
                  <span>Ligar 192 (SAMU) e Estornar Pagamento</span>
                </Button>

                {/* Botão 2: UPA / Pronto-Socorro com estorno automático */}
                <Button
                  variant="outline"
                  onClick={() => handleAbortEmergency(false)}
                  disabled={isAbortingEmergency}
                  className="w-full py-2.5 px-4 text-xs font-semibold text-red-700 hover:bg-red-50 border-red-200 rounded-xl flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <span>Ir à UPA / Pronto-Socorro (Cancelar c/ Estorno)</span>
                </Button>

                {/* Botão 3: Prosseguir com a teleconsulta sob ciência */}
                <Button
                  onClick={handleProceedDespiteEmergency}
                  disabled={isAbortingEmergency}
                  variant="outline"
                  className="w-full text-xs text-neutral-500 hover:text-neutral-800 border-neutral-200 rounded-xl"
                >
                  Estou ciente, prosseguir com teleconsulta expressa
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWaiting = () => (
    <div className="w-full max-w-lg mx-auto px-1 sm:px-0">
      <WaitingQueue
        doctorName={expressStatus?.doctorName || 'Médico Plantonista'}
        specialty="Pronto Atendimento Virtual"
        estimatedWait={estimatedTime}
        queuePosition={queuePosition}
        doctorsOnline={doctorsOnline}
        isDoctorReady={expressStatus?.status === 'ACCEPTED' || expressStatus?.status === 'ROOM_CREATED' || Boolean(doctorFoundNotification)}
        onCancel={handleCancelExpress}
        onEnterConsultation={() => {
          const targetAptId = appointmentId || expressStatus?.appointmentId;
          console.info('[EXPRESS-PATIENT-FE] onEnterConsultation acionado!', {
            targetAptId,
            appointmentId,
            expressStatusAppointmentId: expressStatus?.appointmentId,
            expressId: expressStatus?.expressId,
            expressStatus: expressStatus?.status,
          });
          if (targetAptId) {
            navigate(`/telemedicine/waiting/${targetAptId}`);
          } else {
            console.error('[EXPRESS-PATIENT-FE] Nenhum appointmentId disponível para entrar na sala.');
          }
        }}
      />

      {doctorFoundNotification && (
        <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3 animate-in fade-in">
          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
          <p className="text-sm text-emerald-800 font-medium">{doctorFoundNotification}</p>
        </div>
      )}
    </div>
  );

  const renderRoom = () => (
    <div className="w-full max-w-md mx-auto text-center py-6 sm:py-8 px-2">
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
        <Zap className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600" />
      </div>

      <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-1">Médico Encontrado!</h1>
      <p className="text-xs sm:text-sm text-muted-foreground mb-5 sm:mb-6">
        Dr(a). {expressStatus?.doctorName || 'Plantonista'} aceitou seu atendimento.
      </p>

      <div className="p-4 bg-muted/30 border border-neutral-200 rounded-xl space-y-3">
        <div className="flex items-center justify-center gap-2 text-primary text-xs sm:text-sm font-semibold">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Configurando sala de teleconsulta privativa...</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Você será redirecionado para a sala de atendimento em instantes.
        </p>
      </div>

      {appointmentId && (
        <Button
          onClick={() => navigate(`/telemedicine/waiting/${appointmentId}`)}
          className="mt-6 w-full gap-2 rounded-xl h-12 text-sm font-bold"
        >
          <span>Entrar na Sala Agora</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      )}
    </div>
  );

  const renderConnecting = () => (
    <div className="w-full max-w-md mx-auto text-center py-8 sm:py-12 px-2">
      <Loader2 className="h-12 w-12 sm:h-16 sm:w-16 text-primary animate-spin mx-auto mb-4" />
      <h2 className="text-lg sm:text-xl font-bold text-foreground mb-1">Conectando à Consulta...</h2>
      <p className="text-xs sm:text-sm text-muted-foreground">Aguarde um momento enquanto carregamos o áudio e vídeo.</p>
    </div>
  );

  return (
    <div className="min-h-[85vh] bg-background flex flex-col justify-center py-4 px-3 sm:py-6 sm:px-4">
      {step === 'payment' && renderPayment()}
      {step === 'triage' && renderTriage()}
      {step === 'waiting' && renderWaiting()}
      {step === 'room' && renderRoom()}
      {step === 'consultation' && renderConnecting()}
    </div>
  );
}
